import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { filter, map, Observable, shareReplay } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { RerunTask } from '../rerun-task/rerun-task';
import { buildDocumentFilename } from '../../_helpers/document-filename.util';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { TemplateCategoryEnum } from '../../_enums/template-category.enum';
import { TemplateEntry } from '../../_models/template-entry.model';
import { TemplateCatalogService } from '../../_services/template-catalog.service';
import { UploadDocument } from '../upload-document/upload-document';
import { DocxPreview } from '../document-editor/docx-preview';
import { DocumentTypeEnum } from '../../_enums/document-type.enum';
import { DocumentService } from '../../_services/document.service';
import { TaskService } from '../../_services/task.service';
import { Waiting } from '../waiting/waiting';
import { WorkflowService } from '../../_services/workflow.service';
import { InlineSnackbar } from '../inline-snackbar/inline-snackbar';
import { SnackbarType } from '../../_enums/snackbar-type.enum';

@Component({
  selector: 'app-action-plan',
  imports: [
    TranslateModule,
    UploadDocument,
    RerunTask,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    Waiting,
    FormsModule,
    InlineSnackbar,
  ],
  templateUrl: './action-plan.html',
  styleUrl: './action-plan.scss',
})
export class ActionPlan implements OnChanges, OnInit {
  @ViewChild('uploadDocument') uploadDocument!: UploadDocument;
  @Input() actionPlanFile: File | undefined;
  @Input() werkprocesNummer: number | null = null;
  @Input() clientNummer: number | null = null;

  public actionPlanFileError = false;
  public templateTypes: TemplateEntry[] = [];
  public templatesError = false;
  public selectedTemplateType: TemplateEntry | null = null;
  public DocumentTypeEnum = DocumentTypeEnum;
  public readonly SnackbarType = SnackbarType;
  public isLoading = false;
  public hasGeneratedActionPlan = false;

  private hasTranscriptFile = false;
  private pendingActionPlan$: Observable<File> | null = null;
  private pendingTitle: string | undefined;
  private optionalDocumentsCount = 0;
  private readonly documentService = inject(DocumentService);
  private readonly taskService = inject(TaskService);
  private readonly workflowService = inject(WorkflowService);
  private readonly docxPreview = inject(DocxPreview);
  private readonly templateCatalogService = inject(TemplateCatalogService);

  public get isCreateActionPlanDisabled(): boolean {
    return (
      !this.selectedTemplateType ||
      !this.hasTranscriptFile ||
      this.hasGeneratedActionPlan ||
      this.isLoading
    );
  }

  public ngOnInit(): void {
    this.loadTemplates();
    this.subscribeToTranscriptFile();
    this.subscribeToActionPlanChanged();
    this.subscribeToOptionalDocumentsChanged();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['actionPlanFile'] && this.actionPlanFile) {
      this.isLoading = false;
      this.hasGeneratedActionPlan = true;
    }
  }

  public previewActionPlan(): void {
    if (this.pendingActionPlan$) {
      this.openPreview(this.pendingActionPlan$, this.pendingTitle);
      return;
    }

    if (this.actionPlanFile) {
      this.openPreview(this.actionPlanFile);
    }
  }

  public compareTemplateTypes(t1: TemplateEntry | null, t2: TemplateEntry | null): boolean {
    return t1?.task === t2?.task;
  }

  public createActionPlan(): void {
    const template = this.selectedTemplateType?.task;
    if (!template) return;

    const transcriptFile = this.documentService.getDocumentSnapshot(DocumentTypeEnum.TRANSCRIPT);
    if (!transcriptFile) {
      this.actionPlanFileError = true;
      return;
    }

    // Hide the previous failure while retrying, so the waiting state shows.
    this.actionPlanFileError = false;
    this.isLoading = true;
    const optionalFiles = this.documentService.getOptionalDocumentsValue();
    // The template this plan is made from, even if the selection changes meanwhile.
    const title = this.selectedTemplateType?.label;

    const actionPlan$ = this.taskService
      .createActionPlan(template, transcriptFile, optionalFiles, this.clientNummer, this.werkprocesNummer)
      .pipe(
        map((file: Blob) => new File([file], buildDocumentFilename(title ?? 'actieplan', this.werkprocesNummer), {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        })),
        shareReplay(1),
      );

    this.pendingActionPlan$ = actionPlan$;
    this.pendingTitle = title;

    actionPlan$.subscribe({
      next: (actionPlan) => {
        this.pendingActionPlan$ = null;
        this.documentService.setDocument(DocumentTypeEnum.ACTION_PLAN, actionPlan, title);
        this.actionPlanFileError = false;
        this.isLoading = false;
      },
      error: () => {
        this.pendingActionPlan$ = null;
        this.actionPlanFileError = true;
        this.isLoading = false;
      },
    });

    this.openPreview(actionPlan$, title);
  }

  public onTemplateTypeChange(): void {
    this.workflowService.setStepInProgress(true);
    this.hasGeneratedActionPlan = false;
  }

  private loadTemplates(): void {
    this.templateCatalogService.byCategory(TemplateCategoryEnum.ACTION_PLAN).subscribe({
      next: (templates) => {
        this.templateTypes = templates;
      },
      error: () => {
        this.templatesError = true;
      },
    });
  }

  private openPreview(
    file: File | Observable<File>,
    title = this.documentService.getDocumentTitle(DocumentTypeEnum.ACTION_PLAN),
  ): void {
    this.docxPreview.open({
      file,
      // The template it was made from; a plan the user uploaded shows its file name.
      title,
      edited: (edited) => this.documentService.replaceDocument(DocumentTypeEnum.ACTION_PLAN, edited),
    });
  }

  private subscribeToTranscriptFile(): void {
    this.documentService.getDocument(DocumentTypeEnum.TRANSCRIPT).subscribe((file) => {
      const hadFile = this.hasTranscriptFile;
      this.hasTranscriptFile = !!file;
      if (hadFile !== this.hasTranscriptFile) {
        this.hasGeneratedActionPlan = false;
      }
    });

    this.documentService.getOptionalDocuments().subscribe((files) => {
      const previousCount = this.optionalDocumentsCount;
      this.optionalDocumentsCount = files.length;
      if (previousCount !== this.optionalDocumentsCount) {
        this.hasGeneratedActionPlan = false;
      }
    });
  }

  private subscribeToActionPlanChanged(): void {
    this.documentService
      .isDocumentChanged(DocumentTypeEnum.ACTION_PLAN)
      .pipe(filter((changed) => changed))
      .subscribe(() => {
        this.hasGeneratedActionPlan = false;
      });
  }

  private subscribeToOptionalDocumentsChanged(): void {
    this.documentService
      .isOptionalDocumentsChanged()
      .pipe(filter((changed) => changed))
      .subscribe(() => {
        this.hasGeneratedActionPlan = false;
      });
  }
}
